// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";

// CSS
import "./AutomationLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import { CircularProgress } from "@mui/material";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { RECORDS_PER_PAGE } from "../../../constants/constants";
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// REACT COMPONENT
const AutomationLog = (props) => {
	// When embedded inside another view (e.g. the Automation Logs Dashboard's
	// "Full List" toggle), the host page already provides its own title/padding,
	// and drives refreshes through refreshTrigger (its own refresh button
	// doesn't live inside this component when embedded).
	const { embedded, refreshTrigger } = props;

	// MSAL
	const { instance, accounts } = useMsal();

	// LOGS STATE (current page only - fetched from the server)
	const [automationLogs, setAutomationLogs] = useState([]);
	const [totalCount, setTotalCount] = useState(0);

	const [automationLogsColumns, setAutomationLogsColumns] = useState([]);
	const [automationLogsRows, setAutomationLogsRows] = useState([]);

	// TABLE STATE (owned here so it can drive server-side fetches)
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: RECORDS_PER_PAGE,
	});
	const [columnFilters, setColumnFilters] = useState([]);
	const [sorting, setSorting] = useState([{ id: "created", desc: true }]);

	// LOADING STATES
	const [isLoading, setIsLoading] = useState(false);

	// Tracks the previous filters/sorting so we can reset to page 1 when they change,
	// without firing a duplicate fetch (see effect below)
	const filtersKey = JSON.stringify(columnFilters) + JSON.stringify(sorting);
	const prevFiltersKey = useRef(filtersKey);

	const pageCount = Math.max(1, Math.ceil(totalCount / pagination.pageSize));

	// USE EFFECT
	useEffect(() => {
		if (prevFiltersKey.current !== filtersKey) {
			prevFiltersKey.current = filtersKey;
			if (pagination.pageIndex !== 0) {
				setPagination((prev) => ({ ...prev, pageIndex: 0 }));
				return;
			}
		}
		fetchAutomationLogs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.pageIndex, pagination.pageSize, filtersKey, refreshTrigger]);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [automationLogs]);

	// API HELPER FUNCTIONS
	const fetchAutomationLogs = async () => {
		setIsLoading(true);

		try {
			const sort = sorting[0];
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/automation/all",
				{
					page: pagination.pageIndex + 1,
					pageSize: pagination.pageSize,
					sortBy: sort?.id,
					sortDir: sort?.desc ? "desc" : "asc",
					filters: JSON.stringify(columnFilters),
				}
			);
			setAutomationLogs(response.data.rows);
			setTotalCount(response.data.totalCount);
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	const createColumnData = () => {
		setAutomationLogsColumns([
			{
				accessorKey: "employee_name",
				header: () => <span>Employee</span>,
				label: "Employee",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Time</span>,
				label: "Time",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "tool_name",
				header: () => <span>Tool</span>,
				label: "Tool",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "message",
				header: () => <span>Message</span>,
				label: "Message",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
		]);
	};

	const createRowData = () => {
		setAutomationLogsRows(
			automationLogs.map((log) => ({
				employee_name: log.employee_name,
				created: convertToPST(log.created),
				tool_name: log.tool_name,
				message: log.message,
			}))
		);
	};

	// RENDER
	return (
		<div className={embedded ? "AutomationLog embedded" : "AutomationLog"}>
			{!embedded && (
				<div className="automationLogHeader">
					<div className="automationLogHeaderTitle">
						Automation Logs
					</div>
				</div>
			)}
			{isLoading && automationLogs.length === 0 && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!(isLoading && automationLogs.length === 0) && (
				<div className="tableContainerDiv">
					{/* Only treat this as "no data" when nothing has ever been
					    logged - if a filter narrowed results to zero, keep the
					    table (and its filter controls) visible so the user can
					    adjust or clear the filter. */}
					{totalCount === 0 && columnFilters.length === 0 && (
						<J2NoResultsFound />
					)}
					{automationLogsColumns.length > 0 &&
						!(totalCount === 0 && columnFilters.length === 0) && (
						<TanStackTable
							columnData={automationLogsColumns}
							rowData={automationLogsRows}
							manualPagination
							manualFiltering
							manualSorting
							pagination={pagination}
							onPaginationChange={setPagination}
							columnFilters={columnFilters}
							onColumnFiltersChange={setColumnFilters}
							sorting={sorting}
							onSortingChange={setSorting}
							pageCount={pageCount}
							isLoading={isLoading}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default AutomationLog;
